import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const DEPOSIT_BY_AMENITY = { pool: 300, pool_clubhouse: 300, clubhouse: 300, tennis: 300 };

const SLOTS = ["Half day — Morning (8 AM – 1 PM)", "Half day — Afternoon (2 PM – 8 PM)", "All day (8 AM – 10 PM)"];

const toLocalYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function BookAmenityPage() {
  const { amenity: amenityKey } = useParams();
  const [amenities, setAmenities] = useState([]);
  const [date, setDate] = useState(new Date());
  const [form, setForm] = useState({
    amenity: amenityKey || "",
    time_slot: "",
    guests: 1,
    notes: "",
  });
  const [depositMethod, setDepositMethod] = useState("card"); // "card" | "check" | "later"
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [booked, setBooked] = useState([]);
  const [myProperty, setMyProperty] = useState(null);

  useEffect(() => {
    api.get("/portal/my-property").then(({ data }) => setMyProperty(data)).catch(() => setMyProperty(null));
  }, []);

  const outstandingBalance = Number(myProperty?.balance || 0);
  const hasBalanceBlock = outstandingBalance > 0.01;

  useEffect(() => {
    api.get("/amenities").then(({ data }) => {
      setAmenities(data);
      setForm((f) => (f.amenity ? f : { ...f, amenity: data[0]?.key || "" }));
    });
  }, []);

  useEffect(() => {
    if (!form.amenity) return;
    const dateStr = toLocalYMD(date);
    api.get("/bookings/availability", { params: { amenity: form.amenity, date: dateStr } })
      .then(({ data }) => setBooked(data.booked_slots || []))
      .catch(() => setBooked([]));
  }, [form.amenity, date]);

  const selected = amenities.find((a) => a.key === form.amenity);
  const depositAmount = DEPOSIT_BY_AMENITY[form.amenity] ?? 300;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.time_slot) return toast.error("Please select a time slot");
    setSubmitting(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        amenity: form.amenity,
        date: toLocalYMD(date),
        time_slot: form.time_slot,
        guests: parseInt(form.guests, 10),
        notes: form.notes,
        deposit_payment_method: depositMethod,
      });
      if (depositMethod === "card" && depositAmount > 0) {
        const kind = form.amenity === "pool" ? "pool" : form.amenity === "clubhouse" ? "clubhouse" : "general";
        try {
          const { data } = await api.post("/amenities/deposit-checkout", {
            deposit_kind: kind,
            amount: depositAmount,
            origin_url: window.location.origin,
            booking_id: booking.id,
          });
          window.location.href = data.checkout_url;
          return;
        } catch (err) {
          toast.error("Booking saved, but deposit checkout couldn't start — pay from your dashboard later.");
        }
      }
      setSuccess(true);
      toast.success(
        depositMethod === "check"
          ? "Booking submitted! Mail your $300 check to the board — see next steps in the confirmation email."
          : "Booking request submitted!"
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div data-testid="book-success" className="max-w-md mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#2C4C3B] mx-auto mb-4" />
        <h1 className="font-serif text-3xl text-[#1A2421] mb-2">Request received!</h1>
        <p className="text-[#5C6A64] mb-8">
          The HOA board will review your amenity booking and confirm within 1–2 business days. A copy has been emailed to you.
        </p>
        <div className="flex gap-3 justify-center">
          <Link to="/"><Button variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">Home</Button></Link>
          <Button onClick={() => { setSuccess(false); setForm({ ...form, time_slot: "", notes: "" }); }} className="rounded-full bg-[#2C4C3B]">Book another</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page" className="max-w-6xl mx-auto px-6 py-16">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Amenity Booking</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421] mb-2">Request an amenity reservation.</h1>
      <p className="text-[#5C6A64] mb-10 max-w-xl">
        Reservations are limited to verified homeowners and subject to board approval. A refundable $300 deposit is required to hold your date; the amenity fee is invoiced separately after your booking is approved.
      </p>

      {hasBalanceBlock && (
        <div
          data-testid="balance-block-banner"
          className="mb-8 rounded-2xl border-2 border-[#C86650] bg-[#FDF4F1] p-6 flex flex-col sm:flex-row items-start gap-4"
        >
          <div className="w-11 h-11 rounded-full bg-[#C86650] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-serif text-2xl text-[#1A2421] mb-1">Please clear your balance before booking</p>
            <p className="text-sm text-[#5C6A64] mb-3">
              Your property has an outstanding balance of{" "}
              <span className="font-serif text-[#C86650] text-lg">${outstandingBalance.toFixed(2)}</span>.
              Amenity reservations are reserved for homeowners in good standing. Once your balance is paid (or a settlement is applied), you'll be able to submit this request.
            </p>
            <p className="text-xs text-[#5C6A64] mb-4">
              Need to discuss a payment plan or dispute your balance? Reach the board via the Contact page.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/payments">
                <Button data-testid="balance-block-pay-btn" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-11 px-6">
                  Pay balance
                </Button>
              </Link>
              <Link to="/portal">
                <Button data-testid="balance-block-portal-btn" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] h-11 px-6">
                  Back to portal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl border-[#D5CFC4] lg:col-span-1">
          <CardContent className="p-6">
            <p className="text-sm font-medium text-[#1A2421] mb-3">Select date</p>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
              data-testid="book-calendar"
              className="rounded-xl"
            />
            {selected && (
              <div className="mt-6 p-4 bg-[#EADCC8] rounded-xl">
                <p className="font-serif text-2xl text-[#2C4C3B]">${selected.price.toFixed(2)}</p>
                <p className="text-xs uppercase tracking-wider text-[#5C6A64] mt-1">Reservation fee (invoiced after approval)</p>
                {selected.description && <p className="text-sm text-[#1A2421] mt-3">{selected.description}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-[#D5CFC4] lg:col-span-2">
          <CardContent className="p-8 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Amenity</label>
                <Select value={form.amenity} onValueChange={(v) => setForm({ ...form, amenity: v, time_slot: "" })}>
                  <SelectTrigger data-testid="book-amenity-select" className="mt-2 rounded-xl h-11"><SelectValue placeholder="Choose amenity" /></SelectTrigger>
                  <SelectContent>
                    {amenities.map((a) => (
                      <SelectItem key={a.key} value={a.key}>
                        {a.name} — ${a.price.toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Estimated # of guests</label>
                <Input data-testid="book-guests" type="number" min="1" max="60" value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-2 rounded-xl h-11" />
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Time slot</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                {SLOTS.map((s) => {
                  const isBooked = booked.includes(s);
                  const isSelected = form.time_slot === s;
                  return (
                    <button
                      type="button"
                      key={s}
                      data-testid={`book-slot-${s.replace(/[^a-z0-9]/gi, "-")}`}
                      disabled={isBooked}
                      onClick={() => setForm({ ...form, time_slot: s })}
                      className={`text-sm py-4 rounded-xl border transition-colors ${
                        isBooked
                          ? "bg-[#E5E1D8] text-[#5C6A64] cursor-not-allowed line-through"
                          : isSelected
                          ? "bg-[#2C4C3B] text-white border-[#2C4C3B]"
                          : "bg-white border-[#D5CFC4] text-[#1A2421] hover:border-[#2C4C3B]"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-[#5C6A64] mt-2">The reservation fee is the same for any slot — pricing does not change based on duration.</p>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes / occasion</label>
              <Textarea data-testid="book-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-xl" />
            </div>

            {depositAmount > 0 && (
              <div className="p-4 bg-[#FDF9F1] border border-[#EADCC8] rounded-xl space-y-3" data-testid="deposit-method-group">
                <p className="text-sm font-medium text-[#1A2421]">
                  Refundable deposit — <span className="text-[#C86650]">${depositAmount}</span>
                </p>
                <p className="text-xs text-[#5C6A64]">
                  How would you like to pay the $300 refundable security deposit?
                </p>

                <label
                  data-testid="deposit-method-card"
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${depositMethod === "card" ? "border-[#2C4C3B] bg-white" : "border-transparent hover:bg-white"}`}
                >
                  <input
                    type="radio"
                    name="deposit_method"
                    value="card"
                    checked={depositMethod === "card"}
                    onChange={() => setDepositMethod("card")}
                    className="mt-1"
                    data-testid="deposit-method-card-input"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#1A2421]">Pay by card (Stripe)</p>
                    <p className="text-xs text-[#5C6A64] mt-1">
                      You'll be redirected to Stripe right after submitting. Fastest, no cheque handling.
                    </p>
                    <p className="text-[11px] text-[#C86650] mt-1 font-medium">
                      Note: Card refunds require <b>7–10 business days</b> after your reservation before the deposit is returned to your original card.
                    </p>
                  </div>
                </label>

                <label
                  data-testid="deposit-method-check"
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${depositMethod === "check" ? "border-[#2C4C3B] bg-white" : "border-transparent hover:bg-white"}`}
                >
                  <input
                    type="radio"
                    name="deposit_method"
                    value="check"
                    checked={depositMethod === "check"}
                    onChange={() => setDepositMethod("check")}
                    className="mt-1"
                    data-testid="deposit-method-check-input"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#1A2421]">Pay by check</p>
                    <p className="text-xs text-[#5C6A64] mt-1">
                      Mail a $300 check payable to <b>Saddleridge · Savoy Chase · The Trails HOA</b>. Your booking is held for 5 business days pending arrival. Refund is returned as an uncashed check or new check after your reservation.
                    </p>
                  </div>
                </label>

                <label
                  data-testid="deposit-method-later"
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${depositMethod === "later" ? "border-[#2C4C3B] bg-white" : "border-transparent hover:bg-white"}`}
                >
                  <input
                    type="radio"
                    name="deposit_method"
                    value="later"
                    checked={depositMethod === "later"}
                    onChange={() => setDepositMethod("later")}
                    className="mt-1"
                    data-testid="deposit-method-later-input"
                  />
                  <div>
                    <p className="text-sm font-medium text-[#1A2421]">I'll arrange payment with the board later</p>
                    <p className="text-xs text-[#5C6A64] mt-1">
                      Submit the request first — the board will follow up with payment instructions. Your date is not held until the deposit is received.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <Button data-testid="book-submit" type="submit" disabled={submitting || hasBalanceBlock} className="w-full rounded-full h-12 bg-[#C86650] hover:bg-[#B4553F] text-white disabled:bg-[#D5CFC4] disabled:text-[#5C6A64]">
              {hasBalanceBlock
                ? "Pay balance to unlock booking"
                : submitting
                ? "Submitting…"
                : depositMethod === "card" && depositAmount > 0
                ? `Reserve & pay $${depositAmount} deposit`
                : depositMethod === "check" && depositAmount > 0
                ? "Reserve & mail a check"
                : selected
                ? `Request booking · $${selected.price.toFixed(2)}`
                : "Request booking"}
            </Button>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

